import React, { useState, useCallback, useEffect } from "react";
import { Pressable, ScrollView, TextInput, View } from "react-native";
import Text from "@components/CustomText";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import toast from "@utils/toast";
import {
  commonStyles,
  addressStyles as styles,
  placeholderColor,
} from "./styles";
import { useOnboarding } from "./OnboardingContext";
import { useFlowNavigation } from "./useFlowNavigation";
import {
  submitKycFromContext,
  getSpokeFilters,
  getBlockedErrorMessage,
  type SpokeFilters,
} from "@services/onboardingApi";
import type { StackNavigationProp } from "@react-navigation/stack";
import type { OnboardingStackParamList } from "./types";

interface AddressDetailsProps {
  navigation: StackNavigationProp<OnboardingStackParamList, "AddressDetails">;
}

interface AddressForm {
  flatBuildingFloor: string;
  areaLocality: string;
  pincode: string;
  state: string;
  city: string;
}

const INITIAL_FORM: AddressForm = {
  flatBuildingFloor: "",
  areaLocality: "",
  pincode: "",
  state: "",
  city: "",
};

const AddressDetails: React.FC<AddressDetailsProps> = ({ navigation }) => {
  const { data: onboardingData, updateData } = useOnboarding();
  const { goToNextStep, getDocumentsConfig } = useFlowNavigation();
  const [form, setForm] = useState<AddressForm>(INITIAL_FORM);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filters, setFilters] = useState<SpokeFilters>({
    states: [],
    cities_by_state: {},
  });

  useEffect(() => {
    getSpokeFilters()
      .then(setFilters)
      .catch(() => {});
  }, []);

  const updateField = useCallback((field: keyof AddressForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const onSelectState = useCallback((state: string) => {
    setForm((prev) => ({ ...prev, state, city: "" }));
    setShowStateDropdown(false);
  }, []);

  const onSelectCity = useCallback((city: string) => {
    setForm((prev) => ({ ...prev, city }));
    setShowCityDropdown(false);
  }, []);

  const onContinue = useCallback(async () => {
    if (isSubmitting) return; // TODO: re-enable: || !isFormValid

    setIsSubmitting(true);
    try {
      const nextData = {
        ...onboardingData,
        addressPlot: form.flatBuildingFloor,
        addressArea: form.areaLocality,
        addressPincode: form.pincode,
        addressState: form.state,
        addressCity: form.city,
        currentAddress: `${form.flatBuildingFloor}, ${form.areaLocality}, ${form.city}, ${form.state} - ${form.pincode}`,
      };
      updateData({
        addressPlot: nextData.addressPlot,
        addressArea: nextData.addressArea,
        addressPincode: nextData.addressPincode,
        addressState: nextData.addressState,
        addressCity: nextData.addressCity,
        currentAddress: nextData.currentAddress,
      });

      // Laggan always ends at SignUpSuccessful after address capture.
      await submitKycFromContext(nextData, getDocumentsConfig());
      goToNextStep();
    } catch (err: unknown) {
      const msg = (err as Error)?.message || "";
      if (msg.startsWith("MISSING:")) {
        toast.showLongToast(`Missing ${msg.replace("MISSING:", "")}`);
      } else {
        const axiosError = err as {
          response?: { data?: { message?: string } };
        };
        toast.showLongToast(
          getBlockedErrorMessage(err) ||
            axiosError?.response?.data?.message ||
            "Failed to submit KYC. Please try again.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    form,
    onboardingData,
    updateData,
    goToNextStep,
    getDocumentsConfig,
  ]);

  const renderInput = (
    field: keyof AddressForm,
    label: string,
    placeholder: string,
    options?: {
      keyboardType?: TextInput["props"]["keyboardType"];
      maxLength?: number;
    },
  ) => {
    const isFocused = focusedField === field;
    const value = form[field];
    const hasValue = value.length > 0;

    return (
      <View
        style={[
          commonStyles.inputContainer,
          isFocused && commonStyles.inputContainerFocused,
        ]}
      >
        {hasValue && <Text style={commonStyles.inputLabel}>{label}</Text>}
        <TextInput
          value={value}
          style={commonStyles.inputText}
          placeholder={placeholder}
          placeholderTextColor={placeholderColor}
          keyboardType={options?.keyboardType}
          maxLength={options?.maxLength}
          onChangeText={(text) => updateField(field, text)}
          onFocus={() => setFocusedField(field)}
          onBlur={() => setFocusedField(null)}
          accessibilityLabel={label}
        />
      </View>
    );
  };

  const cities = form.state ? filters.cities_by_state[form.state] ?? [] : [];

  return (
    <>
      <HeaderWithBackAndTitle
        headertitle="Address Details"
        navigation={navigation}
        hideShadow
      />

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {renderInput(
          "flatBuildingFloor",
          "Flat / Building / Floor",
          "Enter Flat / Building / Floor",
        )}
        {renderInput(
          "areaLocality",
          "Area / Locality",
          "Enter Area / Locality",
        )}
        {renderInput("pincode", "Pincode", "Enter Pincode", {
          keyboardType: "number-pad",
          maxLength: 6,
        })}

        {/* State Dropdown */}
        <Pressable
          style={styles.dropdownContainer}
          onPress={() => {
            setShowStateDropdown((v) => !v);
            setShowCityDropdown(false);
          }}
          accessibilityRole="button"
          accessibilityLabel="Select state"
          accessibilityHint="Opens a list of states"
        >
          <View style={styles.dropdownFlex}>
            {form.state ? (
              <>
                <Text style={styles.dropdownLabel}>State</Text>
                <Text variant="body_text_3" style={styles.dropdownValue}>
                  {form.state}
                </Text>
              </>
            ) : (
              <Text variant="body_text_3" style={styles.dropdownPlaceholder}>
                Select State
              </Text>
            )}
          </View>
          <Text style={styles.dropdownChevron}>
            {showStateDropdown ? "▲" : "▼"}
          </Text>
        </Pressable>

        {showStateDropdown && (
          <View style={styles.dropdownList}>
            {filters.states.map((state, idx) => (
              <Pressable
                key={state}
                style={[
                  styles.dropdownListItem,
                  idx === filters.states.length - 1 &&
                    styles.dropdownListItemLast,
                ]}
                onPress={() => onSelectState(state)}
                accessibilityRole="menuitem"
                accessibilityLabel={state}
              >
                <Text variant="body_text_3" style={styles.dropdownListItemText}>
                  {state}
                </Text>
              </Pressable>
            ))}
          </View>
        )}

        {/* City Dropdown */}
        <Pressable
          style={styles.dropdownContainer}
          onPress={() => {
            if (form.state) {
              setShowCityDropdown((v) => !v);
              setShowStateDropdown(false);
            }
          }}
          accessibilityRole="button"
          accessibilityLabel="Select city"
        >
          <View style={styles.dropdownFlex}>
            {form.city ? (
              <>
                <Text style={styles.dropdownLabel}>City</Text>
                <Text variant="body_text_3" style={styles.dropdownValue}>
                  {form.city}
                </Text>
              </>
            ) : (
              <Text variant="body_text_3" style={styles.dropdownPlaceholder}>
                {form.state ? "Select City" : "Please select state first"}
              </Text>
            )}
          </View>
          <Text style={styles.dropdownChevron}>
            {showCityDropdown ? "▲" : "▼"}
          </Text>
        </Pressable>

        {showCityDropdown && cities.length > 0 && (
          <View style={styles.dropdownList}>
            {cities.map((city, idx) => (
              <Pressable
                key={city}
                style={[
                  styles.dropdownListItem,
                  idx === cities.length - 1 && styles.dropdownListItemLast,
                ]}
                onPress={() => onSelectCity(city)}
                accessibilityRole="menuitem"
                accessibilityLabel={city}
              >
                <Text variant="body_text_3" style={styles.dropdownListItemText}>
                  {city}
                </Text>
              </Pressable>
            ))}
          </View>
        )}
      </ScrollView>

      <View style={styles.bottomBar}>
        <Pressable
          style={[
            commonStyles.primaryButton,
            // TODO: re-enable: !isFormValid && commonStyles.primaryButtonDisabled,
          ]}
          disabled={isSubmitting} // TODO: re-enable: !isFormValid ||
          onPress={onContinue}
          accessibilityRole="button"
          accessibilityLabel="Continue"
        >
          <Text variant="title_16" style={commonStyles.primaryButtonText}>
            Continue
          </Text>
        </Pressable>
      </View>
    </>
  );
};

export default AddressDetails;
